import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GoodsProps, UsedInComboRow } from '../../api/Goods';
import * as GoodsApi from '../../api/Goods';
import type { MpAuthOption, MpProduct } from '../../api/MpProducts';
import * as MpProductsApi from '../../api/MpProducts';
import type { RemnantsManageRow } from '../../api/RemnantsManage';
import * as RemnantsManageApi from '../../api/RemnantsManage';
import type { ReviewsProps } from '../../api/Reviews';
import * as ReviewsApi from '../../api/Reviews';
import type { ProductQuestionItem } from '../../api/ProductQuestions';
import * as ProductQuestionsApi from '../../api/ProductQuestions';

export type MpLinkRow = {
  auth: MpAuthOption;
  product: MpProduct | null;
};

export type GoodsDetailBundle = {
  data: GoodsProps;
  brands: { name: string }[];
  mpRows: MpLinkRow[];
  mpLinked: number;
  mpTotal: number;
  stockRows: RemnantsManageRow[];
  stockAvailable: number | null;
  reviews: ReviewsProps[];
  reviewsTotal: number;
  reviewsNew: number;
  questions: ProductQuestionItem[];
  questionsNew: number;
  questionsTotal: number;
  usedInCombos: UsedInComboRow[];
};

export function useGoodsDetail(article: string) {
  const [data, setData] = useState<GoodsProps | null>(null);
  const [brands, setBrands] = useState<{ name: string }[]>([]);
  const [mpAuths, setMpAuths] = useState<MpAuthOption[]>([]);
  const [mpLinks, setMpLinks] = useState<MpProduct[]>([]);
  const [stockRows, setStockRows] = useState<RemnantsManageRow[]>([]);
  const [reviews, setReviews] = useState<ReviewsProps[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsNew, setReviewsNew] = useState(0);
  const [questions, setQuestions] = useState<ProductQuestionItem[]>([]);
  const [questionsTotal, setQuestionsTotal] = useState(0);
  const [usedInCombos, setUsedInCombos] = useState<UsedInComboRow[]>([]);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(true);
  // MP / stocks / combos — not blocked by reviews COUNT
  const [sidePending, setSidePending] = useState(true);
  const [reviewsPending, setReviewsPending] = useState(true);
  const [reviewsCountPending, setReviewsCountPending] = useState(true);
  const [questionsPending, setQuestionsPending] = useState(true);

  const reloadCore = useCallback(async () => {
    if (!article) return;
    setPending(true);
    setSidePending(true);
    setReviewsPending(true);
    setReviewsCountPending(true);
    setQuestionsPending(true);
    setError('');
    try {
      const [goods, brandList] = await Promise.all([
        GoodsApi.getOne(article),
        GoodsApi.getBrands(),
      ]);
      if (!goods) {
        setError('Товар не найден');
        setData(null);
        setSidePending(false);
        setReviewsPending(false);
        setReviewsCountPending(false);
        setQuestionsPending(false);
        return;
      }
      setData(goods);
      setBrands(brandList || []);
    } catch (e: any) {
      setError(e?.message || String(e));
      setSidePending(false);
      setReviewsPending(false);
      setReviewsCountPending(false);
      setQuestionsPending(false);
    } finally {
      setPending(false);
    }
  }, [article]);

  const reloadCoreSide = useCallback(async () => {
    if (!article) return;
    setSidePending(true);
    try {
      const [auths, links, stocks, combos] = await Promise.all([
        MpProductsApi.listAuths().catch(() => [] as MpAuthOption[]),
        MpProductsApi.listForGoods(article).catch(() => [] as MpProduct[]),
        RemnantsManageApi.getRemnantsManage({
          search: article,
          page: 1,
          limit: 50,
        }).catch(() => null),
        GoodsApi.getUsedInCombos(article).catch(() => [] as UsedInComboRow[]),
      ]);
      setMpAuths(auths);
      setMpLinks(links);
      const stockItems = (stocks?.items || []).filter(
        (r) => r.article === article && r.mp_warehouse_id != null,
      );
      setStockRows(stockItems);
      setUsedInCombos(combos || []);
    } finally {
      setSidePending(false);
    }
  }, [article]);

  const reloadReviews = useCallback(() => {
    if (!article) return;
    setReviewsPending(true);
    setReviewsCountPending(true);
    const articleLc = article.toLowerCase();
    void ReviewsApi.getReviewsByFilter({
      article,
      articleExact: true,
      sorting: 'date_desc',
      page: 1,
    })
      .then((rev) => {
        const reviewItems = (rev?.data || []).filter(
          (r) => (r.article || '').toLowerCase() === articleLc,
        );
        setReviews(reviewItems.slice(0, 20));
        setReviewsTotal(Number(rev?.total) || reviewItems.length);
      })
      .catch(() => {
        setReviews([]);
        setReviewsTotal(0);
      })
      .finally(() => setReviewsPending(false));

    void ReviewsApi.getReviewsByFilter({
      article,
      articleExact: true,
      status: ['no_answer', 'created'],
      sorting: 'date_desc',
      page: 1,
      exactTotal: true,
    })
      .then((revOpen) => {
        setReviewsNew(Number(revOpen?.total) || 0);
      })
      .catch(() => setReviewsNew(0))
      .finally(() => setReviewsCountPending(false));
  }, [article]);

  const reloadQuestions = useCallback(async () => {
    if (!article) return;
    setQuestionsPending(true);
    try {
      const ques = await ProductQuestionsApi.getProductQuestions({
        article,
        sorting: 'asked_at_desc',
        page: 1,
        processed: 'all',
      }).catch(() => null);
      setQuestions(ques?.data || []);
      setQuestionsTotal(Number(ques?.total) || ques?.data?.length || 0);
    } finally {
      setQuestionsPending(false);
    }
  }, [article]);

  const reloadSide = useCallback(() => {
    void reloadCoreSide();
    void reloadReviews();
    void reloadQuestions();
  }, [reloadCoreSide, reloadReviews, reloadQuestions]);

  useEffect(() => {
    void reloadCore();
  }, [reloadCore]);

  useEffect(() => {
    if (data) reloadSide();
  }, [data?.article, reloadSide]);

  const mpRows: MpLinkRow[] = useMemo(() => {
    const map = new Map(mpLinks.map((p) => [p.marketplace_auth_id, p]));
    return mpAuths.map((auth) => ({
      auth,
      product: map.get(auth.id) || null,
    }));
  }, [mpAuths, mpLinks]);

  const mpLinked = mpRows.filter((r) => r.product).length;
  const questionsNew = questions.filter(
    (q) =>
      !q.is_processed &&
      (q.workflow_status === 'NEW' || q.workflow_status === 'WAITING'),
  ).length;

  const stockAvailable = useMemo(() => {
    if (!stockRows.length) return null;
    const vals = stockRows
      .map((r) => Number(r.available_remnant))
      .filter((n) => !Number.isNaN(n));
    if (!vals.length) return null;
    return Math.max(...vals);
  }, [stockRows]);

  const refreshMp = useCallback(async () => {
    const [auths, links] = await Promise.all([
      MpProductsApi.listAuths(),
      MpProductsApi.listForGoods(article),
    ]);
    setMpAuths(auths);
    setMpLinks(links);
  }, [article]);

  return {
    pending,
    sidePending,
    reviewsPending,
    reviewsCountPending,
    questionsPending,
    error,
    data,
    setData,
    brands,
    mpRows,
    mpLinked,
    mpTotal: mpRows.length,
    stockRows,
    stockAvailable,
    reviews,
    reviewsTotal,
    reviewsNew,
    questions,
    questionsNew,
    questionsTotal,
    usedInCombos,
    reloadCore,
    reloadSide,
    refreshMp,
  };
}
